import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  Divider,
} from '@mui/material';
import { useUserData } from '../hooks/useUserData';
import { identityApi } from '../api/identityApi';
import { sttApi } from '../api/sttApi';
import { logError } from '../utils/logging';
import Notification from './common/Notification';

const RESET_PASSWORD_COOLDOWN_SECONDS = 60;
const COOLDOWN_STORAGE_KEY = 'passwordResetCooldown';

function Account() {
  const {
    userData,
    error: dataError,
    loading: dataLoading,
  } = useUserData();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [cooldownEndTime, setCooldownEndTime] = useState(() => {
    // Load cooldown from localStorage on mount
    try {
      const stored = localStorage.getItem(COOLDOWN_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const now = Date.now();
        // Check if cooldown is still valid
        if (parsed.endTime > now) {
          return parsed.endTime;
        } else {
          // Cooldown expired, remove it
          localStorage.removeItem(COOLDOWN_STORAGE_KEY);
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
    return null;
  });
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const intervalRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownEndTime) {
      const updateRemaining = () => {
        const now = Date.now();
        const remaining = Math.max(0, Math.ceil((cooldownEndTime - now) / 1000));
        setRemainingSeconds(remaining);
        
        if (remaining === 0) {
          setCooldownEndTime(null);
          localStorage.removeItem(COOLDOWN_STORAGE_KEY);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      };

      updateRemaining();
      intervalRef.current = setInterval(updateRemaining, 1000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [cooldownEndTime]);

  const handleResetPassword = async () => {
    if (!userData?.email) {
      showSnackbar('Email not available', 'error');
      return;
    }

    // Check if still in cooldown
    if (cooldownEndTime && Date.now() < cooldownEndTime) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess('');

    try {
      await identityApi.forgotPassword(userData.email);
      setSuccess('Password change email sent! Please check your email inbox and follow the link to set your new password.');
      showSnackbar('Password change email sent successfully', 'success');
      
      // Start cooldown timer and save to localStorage
      const endTime = Date.now() + (RESET_PASSWORD_COOLDOWN_SECONDS * 1000);
      setCooldownEndTime(endTime);
      localStorage.setItem(COOLDOWN_STORAGE_KEY, JSON.stringify({ endTime }));
    } catch (err) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to send password change email';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
      logError(err, { context: 'Account handleResetPassword' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenStripePortal = async () => {
    setLoading(true);
    setError(null);

    try {
      const returnUrl = `${window.location.origin}/account`;
      const res = await sttApi.createPortalSession(returnUrl);
      const redirectUrl = sttApi.getRedirectUrl(res);
      
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        throw new Error('No redirect URL received from portal session');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to open billing portal';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
      setLoading(false);
      logError(err, { context: 'Account handleOpenStripePortal' });
    }
  };

  if (dataLoading || loading) {
    return (
      <Container maxWidth="md">
        <Typography variant="h6" sx={{ mt: 4 }}>
          Loading...
        </Typography>
      </Container>
    );
  }

  if (dataError) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>
          {dataError}
        </Alert>
      </Container>
    );
  }

  if (!userData) return null;

  const hasSubscription = userData.subscription !== null && userData.subscription !== undefined;

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Account
        </Typography>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Account Information
          </Typography>
          
          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography color="textSecondary" gutterBottom>
              Email
            </Typography>
            <Typography variant="h6" data-test-id="account-email">
              {userData.email}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Change Password
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleResetPassword}
              disabled={loading || (cooldownEndTime && Date.now() < cooldownEndTime)}
              sx={{ mb: 2 }}
              data-test-id="account-change-password-btn"
            >
              {cooldownEndTime && remainingSeconds > 0
                ? `Change Password (${remainingSeconds}s)`
                : 'Change Password'}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {cooldownEndTime && remainingSeconds > 0
                ? `Please wait ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''} before requesting another password change email.`
                : 'Click the button above to receive a password change link via email. You will be able to set a new password using the link.'}
            </Typography>
          </Box>

          {success && (
            <Alert severity="success" sx={{ mt: 2 }} data-test-id="account-change-password-success">
              {success}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} data-test-id="account-change-password-error">
              {error}
            </Alert>
          )}

          {hasSubscription && (
            <>
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Billing & Subscription
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleOpenStripePortal}
                  disabled={loading}
                  sx={{ mb: 2 }}
                  data-test-id="account-manage-billing-btn"
                >
                  Manage Billing in Stripe
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Access your Stripe customer portal to manage your subscription, payment methods, and billing history.
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <Notification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </Container>
  );
}

export default Account;
